import React, { useEffect, useRef, useState } from "react";
import { FlightDetails, ConnectionFlight, FlightSegment } from "../types";

export interface SelectedSegment {
  segment: FlightSegment;
  connectionId: string; // Parent connection's compositeId
}

const flightKey = (flight: FlightDetails) =>
  flight.compositeId || String(flight.id);

const segmentKey = (segment: FlightSegment) =>
  segment.compositeId || String(segment.id);

const connectionKey = (connection: ConnectionFlight) =>
  connection.compositeId ||
  `${connection.segments[0]?.id}_${connection.overallDepartureTime}`;

// Owns the search-results selection model (direct flights + individual
// connection segments) and the toggle helpers. Clears itself whenever the
// search params (not just the page) change.
export function useFlightSelection(
  stopsFilter: string | null,
  searchDate: string,
  selectedDates: string[],
  // Route/flight-number search identifiers. Included so the selection also
  // clears when the user changes "Going to" / "Flying from" / flight number
  // without touching date or stops — otherwise stale picks from the previous
  // search silently carry over into the new result set's post count.
  sourceLabel?: string,
  destinationLabel?: string,
) {
  const [selectedFlights, setSelectedFlights] = useState<FlightDetails[]>([]);
  const [selectedSegments, setSelectedSegments] = useState<SelectedSegment[]>(
    [],
  );

  const prevSearchParams = useRef({
    stopsFilter,
    searchDate,
    selectedDates: JSON.stringify(selectedDates),
    sourceLabel,
    destinationLabel,
  });

  useEffect(() => {
    const paramsChanged =
      prevSearchParams.current.stopsFilter !== stopsFilter ||
      prevSearchParams.current.searchDate !== searchDate ||
      prevSearchParams.current.selectedDates !== JSON.stringify(selectedDates) ||
      prevSearchParams.current.sourceLabel !== sourceLabel ||
      prevSearchParams.current.destinationLabel !== destinationLabel;

    if (paramsChanged) {
      setSelectedFlights([]);
      setSelectedSegments([]);
    }

    prevSearchParams.current = {
      stopsFilter,
      searchDate,
      selectedDates: JSON.stringify(selectedDates),
      sourceLabel,
      destinationLabel,
    };
  }, [stopsFilter, searchDate, selectedDates, sourceLabel, destinationLabel]);

  const toggleFlightSelection = (flight: FlightDetails) => {
    const key = flightKey(flight);
    setSelectedFlights((prev) =>
      prev.some((f) => flightKey(f) === key)
        ? prev.filter((f) => flightKey(f) !== key)
        : [...prev, flight],
    );
  };

  const isSegmentSelected = (
    segment: FlightSegment,
    connectionId: string,
  ): boolean => {
    const key = segmentKey(segment);
    return selectedSegments.some(
      (sel) =>
        segmentKey(sel.segment) === key && sel.connectionId === connectionId,
    );
  };

  const areAllSegmentsSelected = (connection: ConnectionFlight): boolean => {
    const connectionId = connectionKey(connection);
    return connection.segments.every((segment) =>
      isSegmentSelected(segment, connectionId),
    );
  };

  const toggleSegmentSelection = (
    segment: FlightSegment,
    connectionId: string,
    event?: React.MouseEvent,
  ) => {
    if (event) event.stopPropagation(); // Prevent parent click handlers
    const key = segmentKey(segment);
    // Derive from `prev` (pure updater) so a segment can never be added twice.
    setSelectedSegments((prev) => {
      const matches = (sel: SelectedSegment) =>
        segmentKey(sel.segment) === key && sel.connectionId === connectionId;
      return prev.some(matches)
        ? prev.filter((sel) => !matches(sel))
        : [...prev, { segment, connectionId }];
    });
  };

  const toggleAllSegmentsInConnection = (connection: ConnectionFlight) => {
    const connectionId = connectionKey(connection);
    setSelectedSegments((prev) => {
      const selectedKeys = new Set(
        prev
          .filter((sel) => sel.connectionId === connectionId)
          .map((sel) => segmentKey(sel.segment)),
      );
      const allSelected = connection.segments.every((segment) =>
        selectedKeys.has(segmentKey(segment)),
      );
      if (allSelected) {
        return prev.filter((sel) => sel.connectionId !== connectionId);
      }
      const toAdd = connection.segments
        .filter((segment) => !selectedKeys.has(segmentKey(segment)))
        .map((segment) => ({ segment, connectionId }));
      return [...prev, ...toAdd];
    });
  };

  const clearSelection = () => {
    setSelectedFlights([]);
    setSelectedSegments([]);
  };

  // Whether every direct flight and every connection segment currently on the
  // page is selected — drives the "Select all" checkbox's checked state.
  const isAllSelected = (
    pageFlights: FlightDetails[],
    pageConnections: ConnectionFlight[],
  ): boolean => {
    if (pageFlights.length === 0 && pageConnections.length === 0) return false;
    const allFlightsSelected = pageFlights.every((flight) => {
      const key = flightKey(flight);
      return selectedFlights.some((f) => flightKey(f) === key);
    });
    const allConnectionsSelected = pageConnections.every((connection) =>
      areAllSegmentsSelected(connection),
    );
    return allFlightsSelected && allConnectionsSelected;
  };

  // Adds every given direct flight + connection segment to the selection
  // (deduped against what's already picked), leaving everything else as-is.
  // Used by the per-date "Select all", including for flights/connections
  // that live on OTHER pages of the same search.
  const addToSelection = (
    newFlights: FlightDetails[],
    newConnections: ConnectionFlight[],
  ) => {
    setSelectedFlights((prev) => {
      const existingKeys = new Set(prev.map(flightKey));
      const toAdd = newFlights.filter(
        (f) => !existingKeys.has(flightKey(f)),
      );
      return [...prev, ...toAdd];
    });
    setSelectedSegments((prev) => {
      const existingKeys = new Set(
        prev.map((sel) => `${sel.connectionId}::${segmentKey(sel.segment)}`),
      );
      const toAdd: SelectedSegment[] = [];
      newConnections.forEach((connection) => {
        const connectionId = connectionKey(connection);
        connection.segments.forEach((segment) => {
          const key = `${connectionId}::${segmentKey(segment)}`;
          if (!existingKeys.has(key)) toAdd.push({ segment, connectionId });
        });
      });
      return [...prev, ...toAdd];
    });
  };

  // Removes every currently-selected flight/segment matching the given
  // predicates — used to deselect an entire date group, including any
  // flights/segments for that date picked up from other pages by an earlier
  // "select all" (a plain id list from the current page alone wouldn't
  // reach those).
  const removeFromSelectionWhere = (
    flightPredicate: (flight: FlightDetails) => boolean,
    segmentPredicate: (segment: FlightSegment, connectionId: string) => boolean,
  ) => {
    setSelectedFlights((prev) => prev.filter((f) => !flightPredicate(f)));
    setSelectedSegments((prev) =>
      prev.filter((sel) => !segmentPredicate(sel.segment, sel.connectionId)),
    );
  };

  return {
    selectedFlights,
    selectedSegments,
    selectedCount: selectedFlights.length + selectedSegments.length,
    toggleFlightSelection,
    isSegmentSelected,
    areAllSegmentsSelected,
    toggleSegmentSelection,
    toggleAllSegmentsInConnection,
    isAllSelected,
    addToSelection,
    removeFromSelectionWhere,
    clearSelection,
  };
}
